// The client-side half of `backend/src/services/templateVariableMapping.js` — which of a
// template's {{n}} placeholders have a contact field behind them, and whether the mapping is
// still trusted at all.
//
// WHY THIS IS SHARED. Both the Templates page and the campaign launch dialog need this, and both
// used to carry their own copy. One of the two copies was silently broken for its whole life: it
// read `/{{(d+)}}/g`, missing the backslashes, so `d+` matched the literal letter `d` and the
// pattern never matched a real placeholder. `unmappedVariablesOf` therefore always returned an
// empty array, the launch dialog's warning never rendered once, and its Send button was never
// disabled — the server guard was the only thing standing between an unmapped template and a
// campaign where every message is rejected. Two copies of a regex is how that happens, so there
// is now one.
//
// The server remains the authority. Everything here exists so the operator finds out before
// committing rather than through a 400.

/** The `metadata.mappingReview` flag, raised by sync when the provider rewrote a template's body. */
export interface MappingReview {
  /** ISO timestamp of when the flag first went up. Sticky across further edits. */
  since: string;
  /** The body the mapping was last confirmed against. */
  previousBody: string;
  /** The mapping as it stood then, including entries the new body no longer has. */
  previousMapping: Record<string, string>;
  /** Indices added, dropped or relabelled since. May be empty — the wording alone can change. */
  changedIndices: string[];
}

interface MappableTemplate {
  body?: string | null;
  variableMapping?: Record<string, string> | null;
  metadata?: { mappingReview?: MappingReview | null } | null;
}

/**
 * The variable indices a body actually uses, deduplicated, in numeric order.
 *
 * Mirrors `extractBodyVariables` in the backend, including the numeric sort — a lexical one puts
 * {{10}} before {{2}} and would shift every parameter after the ninth.
 */
export function extractVariableIndices(body?: string | null): string[] {
  const matches = String(body ?? '').match(/\{\{(\d+)\}\}/g) || [];
  const indices = new Set(matches.map((m) => m.replace(/[{}]/g, '')));
  return [...indices].sort((a, b) => Number(a) - Number(b));
}

/** Is this variable key a NAMED one ({{name}}) rather than a positional one ({{1}})? */
export function isNamedVariable(key: string): boolean {
  return !/^\d+$/.test(key);
}

/**
 * The variables a template expects at send time — numbered if it has any, otherwise named.
 *
 * Mirrors `extractTemplateVariables` in the backend. `extractVariableIndices` stays numbered-only
 * because the composer only ever produces {{1}}-style bodies and does arithmetic on the indices;
 * this one is for templates synced from the provider console, which may use Meta's NAMED format
 * (`Hi {{name}}`).
 */
export function extractTemplateVariables(body?: string | null): string[] {
  const numbered = extractVariableIndices(body);
  if (numbered.length > 0) return numbered;

  const matches = [...String(body ?? '').matchAll(/\{\{([A-Za-z_][A-Za-z0-9_]*)\}\}/g)];
  return [...new Set(matches.map((m) => m[1]))];
}

/**
 * The template's body variables with no contact field mapped to them.
 *
 * An unmapped variable is not a degraded send but a guaranteed total failure: we would supply
 * fewer parameters than the approved template declares, Meta rejects every message for the
 * mismatch, and the whole campaign fails.
 */
export function unmappedVariablesOf(template?: MappableTemplate | null): string[] {
  if (!template) return [];
  const mapping = template.variableMapping || {};
  return extractTemplateVariables(template.body).filter((index) => !mapping[index]);
}

/**
 * Whether the mapping is waiting on a human to confirm it after a provider-side body edit.
 *
 * Deliberately shaped to read like `unmappedVariablesOf` at the call sites that gate a launch:
 * both take a template, and both answer "is this safe to send as it stands".
 */
export function needsMappingReview(template?: MappableTemplate | null): boolean {
  return Boolean(template?.metadata?.mappingReview);
}
